import type { OrderSummary } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { OrderFlagBadge, OrderStatusBadge } from '@/entities/order';
import { formatCount, formatDateTime, formatWon, koreanDate } from '@/shared/lib';
import { cn, DesignThumbnail, StatusBadge, tableStyles } from '@/shared/ui';

interface OrderRowProps {
  /** 계약 타입을 그대로 받는다. */
  readonly order: OrderSummary;
  readonly onOpen: (id: string) => void;
}

const MUTED_CLASS = 'text-muted-foreground';
const SUB_TEXT_CLASS = 'text-xs text-muted-foreground';

/**
 * 주문 목록의 한 행 — 주문 번호를 누르면 상세로 간다. 상품 칸 왼쪽에 견적 썸네일(상품 목록과 같은 44px).
 * 상태·처리 필요는 배지, 처리 중 취소 요청은 환급 기한을 함께 보인다.
 */
export const OrderRow = ({ order, onOpen }: OrderRowProps): ReactElement => {
  const bankPending =
    order.status === 'PENDING_PAYMENT' &&
    order.paymentProvider === 'BANK_TRANSFER' &&
    order.depositDueAt !== null;
  const overdue = bankPending && new Date(order.depositDueAt ?? '').getTime() < Date.now();
  const hasFlags = order.badges.length > 0 || order.pendingCancelRequest !== null || bankPending;

  return (
    <tr className={tableStyles.row}>
      <td className={tableStyles.td}>
        <button
          type="button"
          className="font-medium whitespace-nowrap underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground focus-visible:decoration-foreground focus-visible:outline-none"
          onClick={() => onOpen(order.id)}
        >
          {order.orderNumber}
        </button>
      </td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap')}>
        {order.paidAt ? formatDateTime(order.paidAt) : <span className={MUTED_CLASS}>미결제</span>}
      </td>
      <td className={cn(tableStyles.td, 'min-w-32')}>
        <span className="flex flex-col">
          <span>{order.companyName ?? order.contactName}</span>
          {order.companyName !== null ? (
            <span className={SUB_TEXT_CLASS}>{order.contactName}</span>
          ) : null}
        </span>
      </td>
      <td className={cn(tableStyles.td, 'min-w-48')}>
        <div className="flex items-center gap-3">
          <DesignThumbnail
            url={order.thumbnailUrl}
            alt={`${order.orderNumber} 디자인 미리보기`}
            className="size-11 shrink-0 rounded-lg bg-muted"
          />
          <span className="flex flex-col">
            <span>{order.productName}</span>
            <span className={SUB_TEXT_CLASS}>{formatCount(order.quantity)}개</span>
          </span>
        </div>
      </td>
      <td className={cn(tableStyles.td, 'text-right whitespace-nowrap tabular-nums')}>
        {formatWon(order.amount)}
      </td>
      <td className={tableStyles.td}>
        <OrderStatusBadge order={order} />
      </td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap tabular-nums')}>
        {order.expectedShipDate ?? <span className={MUTED_CLASS}>미정</span>}
      </td>
      <td className={tableStyles.td}>
        {hasFlags ? (
          <span className="flex flex-col items-start gap-1">
            {bankPending && order.depositDueAt && (
              <StatusBadge tone={overdue ? 'danger' : 'warning'}>
                계좌이체 · 입금 대기 · ~{koreanDate(order.depositDueAt).slice(5).replace('-', '/')}
                {overdue ? ' · 기한 경과' : ''}
              </StatusBadge>
            )}
            {order.badges.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {order.badges.map((badge) => (
                  <OrderFlagBadge key={badge} badge={badge} />
                ))}
              </span>
            ) : null}
            {order.pendingCancelRequest ? (
              <span className={cn(SUB_TEXT_CLASS, 'whitespace-nowrap')}>
                환급 기한 {order.pendingCancelRequest.refundDueDate}
              </span>
            ) : null}
          </span>
        ) : (
          <span className={MUTED_CLASS}>—</span>
        )}
      </td>
    </tr>
  );
};
